import { Container } from '@purpur/library';
import styled, { css } from 'styled-components';
import { match } from 'ts-pattern';

import { purpurFocus } from '@telialt/common-theme/src/mixins';
import { getPurpurColor, getPurpurSpacing, getPurpurTypography } from '@telialt/common-theme/src/purpurSelectors';
import { NavigationItem } from '@telialt/models/src/Navigation';
import { NAVIGATION_OFFSET_CSS_VARIABLE } from '@telialt/utils/src/NavigationUtils/constants';

import { MenuItemBase } from '../MenuItem/MenuItem.styles';
import { displayOption } from '../Navigation.styles';

const SHADOW_OFFSET = 12;

export const StyledMenuItemDropdown = styled(MenuItemBase).attrs({ as: 'button', type: 'button' })``;

export const MenuWrapper = styled.div<{ $open: boolean }>`
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
    padding-bottom: ${SHADOW_OFFSET}px;
    transform: translateY(100%);

    ${purpurFocus()};
`;

export const MenuContainerWrapper = styled.div`
    background-color: ${getPurpurColor('backgroundSecondary')};
    overflow-y: auto;
    max-height: calc(100vh - var(${NAVIGATION_OFFSET_CSS_VARIABLE}));
    position: relative;
    z-index: 2;

    ${purpurFocus()};
`;

export const MenuShadow = styled.div`
    position: absolute;
    bottom: ${SHADOW_OFFSET}px;
    left: 0;
    width: 100%;
    height: 48px;
    box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.1);
    z-index: 0;
    pointer-events: none;
`;

export const MenuContainer = styled(Container)`
    column-width: 13.75rem;
    column-gap: ${getPurpurSpacing('300')};
    padding-top: ${getPurpurSpacing('100')};
    padding-bottom: ${getPurpurSpacing('300')};
`;

type ColumnProps = {
    $displayOption?: NavigationItem['displayOption'];
};

export const StyledMenuColumn = styled.div<ColumnProps>`
    display: flex;
    flex-direction: column;
    break-inside: avoid;
    gap: ${getPurpurSpacing('100')};
    padding: ${getPurpurSpacing('200')} 0;

    ${({ $displayOption }) => displayOption($displayOption)};
`;

export const MenuColumnGroup = styled.section`
    display: flex;
    flex-direction: column;
    gap: ${getPurpurSpacing('100')};
`;

type HeadingProps = {
    $colorOption?: NavigationItem['colorOption'];
    $displayOption?: NavigationItem['displayOption'];
    $isActive?: boolean;
};

export const MenuColumnHeading = styled.div<HeadingProps>`
    display: flex;
    align-items: center;
    align-self: flex-start;
    gap: ${getPurpurSpacing('150')};
    font-size: ${getPurpurTypography('scale200')};
    font-weight: ${getPurpurTypography('weightBold')};
    line-height: ${getPurpurTypography('lineHeightDefault')};

    ${({ $displayOption }) => displayOption($displayOption)};

    color: ${({ $colorOption }) =>
        match($colorOption)
            .with('red', () => getPurpurColor('red500'))
            .otherwise(() => getPurpurColor('textInteractivePrimaryActive'))};
`;

export const MenuColumnHeadingLink = styled(MenuColumnHeading).attrs({ as: 'a' })`
    text-decoration: none;

    ${purpurFocus()};

    ${({ $isActive }) =>
        $isActive &&
        css`
            color: ${getPurpurColor('textInteractiveSelected')};
        `};

    &:hover {
        background-color: ${getPurpurColor('backgroundInteractivePrimaryNegative')};
        color: ${getPurpurColor('textInteractivePrimaryHover')};
    }
    &:active {
        background-color: ${getPurpurColor('backgroundInteractivePrimaryHoverNegative')};
        color: ${getPurpurColor('textInteractivePrimaryActive')};
    }
    &:focus:focus-visible {
        color: ${getPurpurColor('textInteractivePrimary')};
    }
`;

export const StyledMenuColumnItem = styled(MenuItemBase).attrs({ as: 'a' })`
    font-weight: ${getPurpurTypography('weightNormal')};
    padding: 0;
    margin: 0;
    border: none;
    line-height: ${getPurpurTypography('lineHeightLoose')};
    align-items: center;
    align-self: flex-start;
    gap: ${getPurpurSpacing('150')};

    &:hover {
        > span:first-child {
            color: ${getPurpurColor('textInteractivePrimaryHover')};
            background-color: ${getPurpurColor('backgroundInteractivePrimaryNegative')};
        }
    }

    &:active {
        > span:first-child {
            color: ${getPurpurColor('textInteractivePrimaryActive')};
            background-color: ${getPurpurColor('backgroundInteractivePrimaryHoverNegative')};
        }
    }
`;
